import { useCallback, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getSelection, $isRangeSelection, BaseSelection } from 'lexical';
import { $createHeadingNode, $createQuoteNode, $isHeadingNode, $isQuoteNode, HeadingTagType } from '@lexical/rich-text';
import { $getSelectionStyleValueForProperty, $patchStyleText, $setBlocksType } from '@lexical/selection';

/*
 * This hook handles complete state of formating context 
* */
export const useToolbarFormatState = () => {
  const [editor] = useLexicalComposerContext();
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [blockType, setBlockType] = useState<string>('paragraph');
  const [fontSize, setFontSize] = useState<string>('');

  const updateToolbar = useCallback(() => {
    const selection: BaseSelection | null = $getSelection();
    if (!$isRangeSelection(selection)) return;
    const formats: Set<string> = new Set();
    if (selection.hasFormat('bold')) formats.add('bold');
    if (selection.hasFormat('italic')) formats.add('italic');
    if (selection.hasFormat('underline')) formats.add('underline');
    setActiveFormats(formats);

    const anchorNode = selection.anchor.getNode();
    const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();

    if ($isHeadingNode(element)) setBlockType(element.getTag());
    else if ($isQuoteNode(element)) setBlockType('quote');
    else setBlockType('paragraph');

    setFontSize($getSelectionStyleValueForProperty(selection, 'font-size', ''));
  }, []);

  useEffect(() => editor.registerUpdateListener(({ editorState }) => {
    editorState.read(() => updateToolbar())
  }), [editor, updateToolbar]);

  const applyBlockType = (type: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (type === 'quote') $setBlocksType(selection, () => $createQuoteNode());
      else if (type === 'paragraph') $setBlocksType(selection, () => $createParagraphNode());
      else $setBlocksType(selection, () => $createHeadingNode(type as HeadingTagType));
    });
  };

  const applyFontSize = (size: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      $patchStyleText(selection, { 'font-size': size || null });
    });
  };

  return { activeFormats, blockType, fontSize, applyBlockType, applyFontSize };
};
